const defaultWindowSize = 120;
const percentile = (values, p) => {
    var _a;
    if (values.length === 0) {
        return 0;
    }
    const index = Math.floor((p / 100) * (values.length - 1));
    return (_a = values[Math.min(values.length - 1, Math.max(0, index))]) !== null && _a !== void 0 ? _a : 0;
};
const buildFrameStats = (samples, lastMs) => {
    var _a, _b;
    if (samples.length === 0) {
        return {
            count: 0,
            minMs: 0,
            maxMs: 0,
            avgMs: 0,
            p50Ms: 0,
            p95Ms: 0,
            lastMs,
        };
    }
    const sorted = [...samples].sort((a, b) => a - b);
    const total = samples.reduce((sum, value) => sum + value, 0);
    const count = samples.length;
    return {
        count,
        minMs: (_a = sorted[0]) !== null && _a !== void 0 ? _a : 0,
        maxMs: (_b = sorted[sorted.length - 1]) !== null && _b !== void 0 ? _b : 0,
        avgMs: total / count,
        p50Ms: percentile(sorted, 50),
        p95Ms: percentile(sorted, 95),
        lastMs,
    };
};
export class FrameInstrumentation {
    constructor(options = {}) {
        var _a;
        this.sampleCount = 0;
        this.writeIndex = 0;
        this.lastMs = 0;
        this.windowSize = Math.max(1, (_a = options.windowSize) !== null && _a !== void 0 ? _a : defaultWindowSize);
        this.samples = new Array(this.windowSize).fill(0);
    }
    recordFrame(frameMs) {
        this.lastMs = frameMs;
        this.samples[this.writeIndex] = frameMs;
        this.writeIndex = (this.writeIndex + 1) % this.windowSize;
        this.sampleCount = Math.min(this.sampleCount + 1, this.windowSize);
    }
    recordSchedulerStats(stats) {
        this.recordFrame(stats.frameMs);
        this.lastQueueDepths = stats.queueDepths;
        this.lastDropped = stats.dropped;
        this.lastProcessed = stats.processed;
    }
    getSnapshot() {
        const samples = this.sampleCount === this.windowSize
            ? [...this.samples]
            : this.samples.slice(0, this.sampleCount);
        return {
            frame: buildFrameStats(samples, this.lastMs),
            queueDepths: this.lastQueueDepths ? Object.assign({}, this.lastQueueDepths) : undefined,
            dropped: this.lastDropped ? Object.assign({}, this.lastDropped) : undefined,
            processed: this.lastProcessed ? Object.assign({}, this.lastProcessed) : undefined,
        };
    }
}
export class FrameWatchdog {
    constructor(options = {}) {
        var _a, _b, _c, _d;
        this.level = 0;
        this.recoverStreak = 0;
        this.maxThrottleLevel = (_a = options.maxThrottleLevel) !== null && _a !== void 0 ? _a : 3;
        this.p95ThresholdMs = (_b = options.p95ThresholdMs) !== null && _b !== void 0 ? _b : 24;
        this.p95RecoverMs = (_c = options.p95RecoverMs) !== null && _c !== void 0 ? _c : 18;
        this.consecutiveRecoverFrames = (_d = options.consecutiveRecoverFrames) !== null && _d !== void 0 ? _d : 90;
        this.queueDepthThreshold = options.queueDepthThreshold;
        this.droppedThreshold = options.droppedThreshold;
    }
    getLevel() {
        return this.level;
    }
    evaluate(snapshot) {
        const p95 = snapshot.frame.p95Ms;
        const queueDepths = snapshot.queueDepths;
        const dropped = snapshot.dropped;
        const queueExceeded = this.queueDepthThreshold !== undefined &&
            queueDepths &&
            Object.values(queueDepths).some((depth) => depth >= this.queueDepthThreshold);
        const droppedTotal = dropped && this.droppedThreshold !== undefined
            ? Object.values(dropped).reduce((sum, value) => sum + value, 0)
            : 0;
        const droppedExceeded = this.droppedThreshold !== undefined && droppedTotal >= this.droppedThreshold;
        if (p95 >= this.p95ThresholdMs || queueExceeded || droppedExceeded) {
            this.recoverStreak = 0;
            if (this.level < this.maxThrottleLevel) {
                this.level += 1;
                const reason = queueExceeded
                    ? `queue depth >= ${this.queueDepthThreshold}`
                    : droppedExceeded
                        ? `dropped >= ${this.droppedThreshold}`
                        : `p95 ${p95.toFixed(1)}ms >= ${this.p95ThresholdMs}ms`;
                return { level: this.level, action: "escalate", reason };
            }
            return { level: this.level, action: "none" };
        }
        if (this.level > 0 && p95 <= this.p95RecoverMs) {
            this.recoverStreak += 1;
            if (this.recoverStreak >= this.consecutiveRecoverFrames) {
                this.level -= 1;
                this.recoverStreak = 0;
                return {
                    level: this.level,
                    action: "recover",
                    reason: `p95 ${p95.toFixed(1)}ms <= ${this.p95RecoverMs}ms`,
                };
            }
        }
        else {
            this.recoverStreak = 0;
        }
        return { level: this.level, action: "none" };
    }
}
